import { lazy } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { isLoggedIn, getUser } from './api';
import Layout from './components/layout/Layout';

const LoginPage = lazy(() => import('./pages/LoginPage'));
const OnboardingPage = lazy(() => import('./pages/OnboardingPage'));
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const PlanPage = lazy(() => import('./pages/PlanPage'));
const RecipesPage = lazy(() => import('./pages/RecipesPage'));
const RecipeDetailPage = lazy(() => import('./pages/RecipeDetailPage'));
const ShoppingPage = lazy(() => import('./pages/ShoppingPage'));
const ShoppingPromptPage = lazy(() => import('./pages/ShoppingPromptPage'));
const ProfilePage = lazy(() => import('./pages/ProfilePage'));
const MealStructurePage = lazy(() => import('./pages/MealStructurePage'));
const ProductsPage = lazy(() => import('./pages/ProductsPage'));
const DailyMealsPage = lazy(() => import('./pages/DailyMealsPage'));
const PreferencesPage = lazy(() => import('./pages/PreferencesPage'));
const PlanningRulesPage = lazy(() => import('./pages/PlanningRulesPage'));
const TrackingPage = lazy(() => import('./pages/TrackingPage'));
const MorePage = lazy(() => import('./pages/MorePage'));
const HouseholdPage = lazy(() => import('./pages/HouseholdPage'));
const ActivityTemplatesPage = lazy(() => import('./pages/ActivityTemplatesPage'));
const ReviewPage = lazy(() => import('./pages/ReviewPage'));
const PantryPage = lazy(() => import('./pages/PantryPage'));
// StandardIngredientsPage merged into PantryPage
const CustomPromptsPage = lazy(() => import('./pages/CustomPromptsPage'));
const ChangelogPage = lazy(() => import('./pages/ChangelogPage'));
const BringSettingsPage = lazy(() => import('./pages/BringSettingsPage'));
const PlansManagePage = lazy(() => import('./pages/PlansManagePage'));
const CreditsPage = lazy(() => import('./pages/CreditsPage'));
const HouseholdSetupPage = lazy(() => import('./pages/HouseholdSetupPage'));
const CookingDayPage = lazy(() => import('./pages/CookingDayPage'));

function RequireAuth({ children }: { children: React.ReactNode }) {
  if (!isLoggedIn()) {
    return <Navigate to="/login" replace />;
  }
  const user = getUser() as { onboarding_completed?: boolean | number; household_id?: number } | null;
  if (user && !user.onboarding_completed) {
    return <Navigate to="/onboarding" replace />;
  }
  if (user && !user.household_id) {
    return <Navigate to="/household-setup" replace />;
  }
  return <>{children}</>;
}

export default function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/onboarding" element={<OnboardingPage />} />
      <Route path="/household-setup" element={<HouseholdSetupPage />} />
      <Route element={<RequireAuth><Layout /></RequireAuth>}>
        <Route index element={<DashboardPage />} />
        <Route path="plan" element={<PlanPage />} />
        <Route path="cooking-day" element={<CookingDayPage />} />
        <Route path="recipes" element={<RecipesPage />} />
        <Route path="recipes/:id" element={<RecipeDetailPage />} />
        <Route path="custom-recipes/:id" element={<RecipeDetailPage />} />
        <Route path="shopping" element={<ShoppingPage />} />
        <Route path="shopping/prompt" element={<ShoppingPromptPage />} />
        <Route path="tracking" element={<TrackingPage />} />
        <Route path="review" element={<ReviewPage />} />
        <Route path="more" element={<MorePage />} />
        <Route path="settings" element={<MorePage />} />
        <Route path="settings/profile" element={<ProfilePage />} />
        <Route path="settings/structure" element={<MealStructurePage />} />
        <Route path="settings/products" element={<ProductsPage />} />
        <Route path="settings/daily-meals" element={<DailyMealsPage />} />
        <Route path="settings/preferences" element={<PreferencesPage />} />
        <Route path="settings/planning-rules" element={<PlanningRulesPage />} />
        <Route path="settings/household" element={<HouseholdPage />} />
        <Route path="settings/activities" element={<ActivityTemplatesPage />} />
        <Route path="pantry" element={<PantryPage />} />
        <Route path="settings/standard-ingredients" element={<Navigate to="/pantry" replace />} />
        <Route path="settings/prompts" element={<CustomPromptsPage />} />
        <Route path="settings/bring" element={<BringSettingsPage />} />
        <Route path="settings/plans" element={<PlansManagePage />} />
        <Route path="settings/credits" element={<CreditsPage />} />
        <Route path="changelog" element={<ChangelogPage />} />
      </Route>
    </Routes>
  );
}
